import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Box, Button, SpaceBetween } from '@amzn/awsui-components-console';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackMessage?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React Error Boundary that catches unhandled render errors and displays
 * a recovery UI instead of a white screen. Users can retry by clicking
 * "Reload" or navigate home.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = (): void => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleGoHome = (): void => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box padding={{ horizontal: 'xl', vertical: 'xxxl' }}>
          <SpaceBetween size="l">
            <Alert type="error" header="Something went wrong">
              {this.props.fallbackMessage ||
                'An unexpected error occurred. Please try reloading the page.'}
            </Alert>
            {this.state.error && (
              <Box color="text-status-inactive" fontSize="body-s">
                {this.state.error.message}
              </Box>
            )}
            <SpaceBetween size="s" direction="horizontal">
              <Button variant="primary" onClick={this.handleReload}>
                Reload page
              </Button>
              <Button variant="normal" onClick={this.handleGoHome}>
                Go to home
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
